import { createSelector } from 'reselect';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { type StoreState } from '../types/StoreState';
import {
  type PaymentMethodFundingSource,
  PaymentMethodFundingSources,
  type PayPalFundingSource,
} from '../types/PaymentsButtonViewConfig';
import {
  selectIsCreditBtnAvailable,
  selectIsPayLaterBtnAvailable,
  selectIsPayPalButtonAvailable,
  selectIsVenmoBtnAvailable,
} from './payPal';
import { selectApplePayButtonAvailable } from './applePay';
import { selectLoadPayPalScriptState } from './requestsState';
import { selectGooglePayButtonAvailable } from './googlePay';

export const selectPaymentState = (state: StoreState) => state.payment;

export const selectPayPalPaymentData = (state: StoreState) => selectPaymentState(state)?.[PaymentMethod.PayPal];

export const selectPayPalOrderId = (state: StoreState) => selectPayPalPaymentData(state)?.orderId;

export const selectPayPalShippingContact = (state: StoreState) => selectPayPalPaymentData(state)?.shippingContact;

export const selectAvailablePayPalFundingSources = createSelector(
  selectLoadPayPalScriptState,
  selectIsPayPalButtonAvailable,
  selectIsPayLaterBtnAvailable,
  selectIsCreditBtnAvailable,
  selectIsVenmoBtnAvailable,
  (scriptState, isPayPalAvailable, isPayLaterAvailable, isPayPalCreditAvailable, isVenmoAvailable) => {
    const list: PayPalFundingSource[] = [];

    if (scriptState === 'fulfilled') {
      const { PAYPAL, PAYLATER, CREDIT, VENMO } = window.paypal.FUNDING;

      if (isPayPalAvailable) {
        list.push(PAYPAL);
      }

      if (isPayLaterAvailable) {
        list.push(PAYLATER);
      }

      if (isPayPalCreditAvailable) {
        list.push(CREDIT);
      }

      if (isVenmoAvailable) {
        list.push(VENMO);
      }
    }

    return list;
  },
);

export const selectAvailableMethods = createSelector(
  selectGooglePayButtonAvailable,
  selectApplePayButtonAvailable,
  selectAvailablePayPalFundingSources,
  (isGPayAvailable, isApplePayAvailable, payPalMethodsSources) => [
    ...(isGPayAvailable ? [PaymentMethod.GooglePay] : []),
    ...(isApplePayAvailable ? [PaymentMethod.ApplePay] : []),
    ...(payPalMethodsSources.length > 0 ? [PaymentMethod.PayPal] : []),
  ],
);

export const selectAvailableFundingSources = createSelector(
  selectAvailableMethods,
  selectAvailablePayPalFundingSources,
  (availableMethods, payPalMethods) => {
    const paymentMethods: PaymentMethodFundingSource[] = [];

    if (availableMethods.includes(PaymentMethod.GooglePay)) {
      paymentMethods.push(PaymentMethodFundingSources.GooglePay);
    }

    if (availableMethods.includes(PaymentMethod.ApplePay)) {
      paymentMethods.push(PaymentMethodFundingSources.ApplePay);
    }

    if (availableMethods.includes(PaymentMethod.PayPal)) {
      paymentMethods.push(...payPalMethods);
    }

    return paymentMethods;
  },
);
